import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../component/Button";
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";

/**  모바일에서만: 60% 보이면 아래→위로 등장(한 번만) */
function useMobileInViewOnce<T extends HTMLElement>(
  options: IntersectionObserverInit = { threshold: 0.6 },
) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  const isMobile = useResponsiveMobile(900);
  useEffect(() => {
    if (!isMobile) return;

    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return { ref, inView };
}

export const Section6 = () => {
  const isMobile = useResponsiveMobile(900);
  const navigate = useNavigate();

  const { ref: textRef, inView: textInView } = useMobileInViewOnce<HTMLDivElement>({
    threshold: 0.6,
  });
  const { ref: btnRef, inView: btnInView } = useMobileInViewOnce<HTMLDivElement>({
    threshold: 0.6,
  });

  const enterBase = "transition-all duration-700 ease-out will-change-transform";
  const enterHidden = "opacity-0 translate-y-8";
  const enterShown = "opacity-100 translate-y-0";

  const mobileEnter = (on: boolean) =>
    isMobile ? `${enterBase} ${on ? enterShown : enterHidden}` : "";

  return (
    <section
      className="flex flex-col justify-center items-center gap-[50px] pt-[120px] md:pt-[0px] md:gap-[60px] pb-[120px] md:pb-[160px]"
      style={
        isMobile
          ? {
              backgroundImage:
                "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 20%, rgba(255,255,255,0.6) 45%, rgba(255,255,255,0) 70%), url('/assets/images/effect/gradient.png')",
              backgroundRepeat: "no-repeat, no-repeat",
              backgroundSize: "100% 240px, cover",
              backgroundPosition: "top, center",
            }
          : { backgroundColor: "#fff" }
      }
    >
      {/*  모바일 스크롤 등장: 텍스트 블록 */}
      <div ref={textRef} className={mobileEnter(textInView)}>
        <div className="flex flex-col md:gap-8 gap-3">
          <span className="font-bold text-[28px] md:text-[36px] text-center ">
            품 도입을 {isMobile && <br />} 검토 중이신가요?
          </span>
          <span className="font-normal text-[20px] md:text-[28px] text-center text-[#4D4F58]">
            1:1 도입 상담 또는 품 소개서 {isMobile && <br />} 확인으로 더 자세히 알아보세요
          </span>
        </div>
      </div>

      {/*  모바일 스크롤 등장: 버튼 블록 */}
      <div
        ref={btnRef}
        className={[
          "flex flex-col md:flex-row gap-4 md:gap-3 items-center w-full px-6 md:px-0 justify-center",
          mobileEnter(btnInView),
        ].join(" ")}
      >
        <Button
          id="btn-bottom-apply"
          onClick={() => navigate("/inquiry-form?type=adoption")}
          size="md"
          className={`md:h-20 h-[58px] md:text-[20px] text-[16px] ${
            isMobile
              ? `w-full px-8 rounded-[70px] border-[3px] border-transparent
        [background-origin:border-box]
        [background-clip:padding-box,border-box]
        bg-[linear-gradient(#fff,#fff),linear-gradient(90deg,#0079FF_0%,#00FF44_56.09%,#FFC400_100%)]
        text-[#0079FF]`
              : "btn-split-color-spin"
          } relative px-8 rounded-full bg-white text-[#0079FF] overflow-hidden`}
        >
          <span className="btn-inner absolute inset-[2px] rounded-[70px] bg-white" />
          <span className="relative z-10">품 도입 문의</span>
        </Button>

        <Button
          id="btn-bottom-doc"
          size={isMobile ? "md" : "sm"}
          className="md:h-20 h-[58px] md:text-[20px] text-[16px] md:min-w-[256px] rounded-[70px] px-8 bg-[#000] w-full md:w-fit"
          onClick={() => {
            navigate("/inquiry-form?type=introduction");
          }}
        >
          소개자료 다운받기
        </Button>
      </div>
    </section>
  );
};
